import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';

import api from '../api';

const initialFormState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const resetState = () => {
    setFormValues(initialFormState);
    setErrors({});
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    resetState();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});

    if (formValues.newPassword !== formValues.confirmPassword) {
      setErrors({
        confirmPassword: 'Passwords do not match.',
      });
      return;
    }

    setSubmitting(true);

    try {
      await api.post('/change-password/', {
        old_password: formValues.currentPassword,
        new_password: formValues.newPassword,
        confirm_password: formValues.confirmPassword,
      });

      toast({
        title: 'Password updated',
        description: 'Your password has been changed successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      resetState();
      onClose();
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData) {
        if (typeof responseData === 'string') {
          toast({
            title: 'Failed to change password',
            description: responseData,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          setErrors({
            currentPassword: responseData.old_password?.[0],
            newPassword: responseData.new_password?.[0],
            confirmPassword:
              responseData.confirm_password?.[0] || responseData.non_field_errors?.[0],
          });
        }
      } else {
        toast({
          title: 'Failed to change password',
          description: 'An unexpected error occurred. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit}>
        <ModalHeader>Change Password</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isInvalid={Boolean(errors.currentPassword)} mb={4}>
            <FormLabel>Current Password</FormLabel>
            <Input
              type="password"
              value={formValues.currentPassword}
              onChange={handleChange('currentPassword')}
              required
            />
            <FormErrorMessage>{errors.currentPassword}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.newPassword)} mb={4}>
            <FormLabel>New Password</FormLabel>
            <Input
              type="password"
              value={formValues.newPassword}
              onChange={handleChange('newPassword')}
              required
            />
            <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.confirmPassword)}>
            <FormLabel>Confirm New Password</FormLabel>
            <Input
              type="password"
              value={formValues.confirmPassword}
              onChange={handleChange('confirmPassword')}
              required
            />
            <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={submitting}>
            Cancel
          </Button>
          <Button colorScheme="blue" type="submit" isLoading={submitting}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangePasswordModal;

